import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { User, Permission } from '@/domain/entities/user.entity';

interface AuthState {
  user: User | null;
  permissions: Permission[];
  accessToken: string | null;
  refreshToken: string | null;
  accessTokenExpiresAt: number | null;
  isAuthenticated: boolean;

  setAuth: (data: {
    user: User;
    permissions: Permission[];
    accessToken: string;
    refreshToken: string;
    accessTokenExpiresAt: number;
  }) => void;

  setTokens: (data: {
    accessToken: string;
    refreshToken: string;
    accessTokenExpiresAt: number;
  }) => void;

  logout: () => void;

  hasPermission: (moduleCode: string, action: string) => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      permissions: [],
      accessToken: null,
      refreshToken: null,
      accessTokenExpiresAt: null,
      isAuthenticated: false,

      setAuth: (data) => {
        set({
          user: data.user,
          permissions: data.permissions,
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          accessTokenExpiresAt: data.accessTokenExpiresAt,
          isAuthenticated: true,
        });
      },

      setTokens: (data) => {
        set({
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          accessTokenExpiresAt: data.accessTokenExpiresAt,
        });
      },

      logout: () => {
        set({
          user: null,
          permissions: [],
          accessToken: null,
          refreshToken: null,
          accessTokenExpiresAt: null,
          isAuthenticated: false,
        });
      },

      hasPermission: (moduleCode: string, action: string) => {
        const { permissions } = get();
        const required = `${moduleCode}:${action}`;
        return permissions.some(
          (p) => `${p.moduleCode}:${p.action}` === required,
        );
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        user: state.user,
        permissions: state.permissions,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        accessTokenExpiresAt: state.accessTokenExpiresAt,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);
